import {
  Component,
  computed,
  effect,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { AccessRevokedNoticeService } from '../../access-revoked-notice.service';
import * as fromLayout from '../../layout';
import { bindPageTitle } from '../../page-title';
import { KnowledgeModalComponent } from '../assistants/knowledge-modal.component';
import { SkillBuilderHostComponent } from '../assistants/skill-builder-host.component';
import {
  assistantsFeature,
  AssistantsState,
} from '../assistants/store/assistants.reducer';
import { IntroductionComponent } from '../components/introduction.component';
import { TopBarMobileResponsive } from '../components/top-bar.mobile.responsive';
import * as fromShared from '../shared';
import { teammatesFeature, TeammatesState } from './store';
import { TeammateAssistantComponent } from './teammate-assistant.component';
import { getTeammateVisualFromParts } from './teammate-avatar.config';

@Component({
  imports: [
    TopBarMobileResponsive,
    TranslateModule,
    TeammateAssistantComponent,
    IntroductionComponent,
    SkillBuilderHostComponent,
    KnowledgeModalComponent,
  ],
  template: `
    <div class="bg-gradient-e-vertical h-svh w-full flex flex-col">
      @if (isMobile()) {
        <squadbox-top-bar-mobile
          class="bg-shades-white"
          (chevronLeftButtonClicked)="handleChevronLeftButtonCTA()"
        >
        </squadbox-top-bar-mobile>
      }

      @let teammate = selectedTeammate();
      <!--
        We want the scrollbar to be flush with the right side of the screen, not flush with the right side of the content.
        If the size of the viewport is > 1320px, this one-element div ensures, that the scrollbar is positioned correctly
      -->
      <div class="w-full flex flex-col overflow-y-auto fade-in-up-subtle">
        @if (teammate) {
          <div
            class="
            flex flex-col gap-6
            w-full max-w-370 self-center
            tablet-portrait:px-20 px-6
            tablet-portrait:pt-22 pt-6
            pb-6
          "
          >
            <squadbox-introduction
              [teammate]="true"
              [visual]="selectedTeammateVisual()!"
              [title]="teammate.welcomeMessage"
              [subtitle]="teammate.purposeMessage"
            />

            <!-- Teammate Assistants -->
            <div
              class="
              grid gap-2 tablet-portrait:gap-4
              grid-cols-3 max-[1194px]:grid-cols-2 max-[650px]:grid-cols-1
            "
            >
              @for (assistant of teammate.assistants; track assistant.uuid) {
                <squadbox-teammate-assistant-card
                  class="group"
                  [cardData]="assistant"
                  [isMobile]="isMobile()"
                />
              }
            </div>
          </div>
        }
      </div>

      <!-- Both pencil destinations (SB-3650 / T5, KD-32). Neither overlay was
           mounted on this route, so both branches of the pencil were dead
           clicks here. The host owns the builder's @if and both dismissal
           prompts; the knowledge modal is created per opening, which is what
           resets its transaction id and staged list. -->
      <squadbox-skill-builder-host />
      @if (knowledgeModal().kind !== 'closed') {
        <squadbox-knowledge-modal />
      }
    </div>
  `,
})
export class TeammatePage {
  private readonly layoutStore = inject(Store<fromLayout.LayoutState>);
  private readonly teammatesStore = inject<Store<TeammatesState>>(Store);
  private readonly assistantsStore = inject<Store<AssistantsState>>(Store);
  private readonly accessRevokedNotice = inject(AccessRevokedNoticeService);
  private readonly routerService = inject(fromShared.RouterService);
  private readonly mobileNavigation = inject(
    fromShared.MobileNavigationService,
  );

  public readonly slug = input.required<string>();

  private readonly areTeammatesLoaded = this.teammatesStore.selectSignal(
    teammatesFeature.selectLoaded,
  );

  public readonly isMobile = this.layoutStore.selectSignal(
    fromLayout.layoutFeature.selectIsMobile,
  );
  /** Edit knowledge modal state (SB-3650 / T2 ships the slice, T3 the modal). */
  public readonly knowledgeModal = this.assistantsStore.selectSignal(
    assistantsFeature.selectKnowledgeModal,
  );
  private readonly builderHost = viewChild(SkillBuilderHostComponent);
  public readonly selectedTeammate = computed(() => {
    const slug = this.slug();

    return slug
      ? this.teammatesStore.selectSignal(
          teammatesFeature.selectTeammateBySlug(slug),
        )()
      : undefined;
  });

  /**
   * The selected teammate's resolved visual, precomputed.
   *
   * This page is CheckAlways, so calling the resolver straight from the
   * template rebuilt the object on every change-detection pass, rewriting the
   * avatar's input signal each tick and re-dirtying its initialization effect
   * (which resets any in-flight hover animation). SB-3519 widened this from
   * the avatar config to the visual union, which is built with spreads and so
   * is just as reference-unstable — the memoization matters as much as before,
   * not less.
   */
  public readonly selectedTeammateVisual = computed(() => {
    const teammate = this.selectedTeammate();

    return teammate ? getTeammateVisualFromParts(teammate) : undefined;
  });

  constructor() {
    bindPageTitle(() => this.selectedTeammate()?.name);

    // Remember the slug we've actually resolved a teammate for, so a live
    // revocation (it was visible, now it's gone) is distinguishable from a
    // never-accessible slug (a typo or stale link that was never theirs).
    let resolvedSlug: string | null = null;

    effect(() => {
      const slug = this.slug();
      if (!slug) return;

      const loaded = this.areTeammatesLoaded();
      if (!loaded) return;

      const teammate = this.selectedTeammate();
      if (teammate) {
        resolvedSlug = slug;
        return;
      }

      // Absent + loaded: if we had resolved this exact slug, the teammate was
      // revoked out from under the user → landing + "access removed" toast.
      // Otherwise it was never theirs → a plain 404, as before.
      if (resolvedSlug === slug) {
        void this.accessRevokedNotice.notify('TOAST.ACCESS_REVOKED.TEAMMATE');
      } else {
        void this.routerService.navigateToNotFound();
      }
    });
  }

  public async handleChevronLeftButtonCTA(): Promise<void> {
    await this.mobileNavigation.open();
  }

  /**
   * CanDeactivate hook, delegated to the builder host exactly as the assistants
   * page does (SB-3650 / T5, KD-32). Registered on this route in
   * `app.routes.ts`;
   *
   * `true` when the host is not present. Unlike the pre-T5 code this replaced,
   * that branch is not a live case: the host is rendered UNCONDITIONALLY, so it
   * resolves whenever this page has rendered at all, whereas the old
   * `viewChild(AssistantBuilderPage)` sat under an `@if` and was legitimately
   * undefined while the slice was open. If it is ever made conditional, this
   * branch has to close both slices the way the host does.
   *
   * It also runs on a teammate-to-teammate slug change, because this route sets
   * `runGuardsAndResolvers: 'paramsOrQueryParamsChange'` and the component is
   * reused. With a clean open builder the allow path closes it, which is what
   * we want — the slice lives on the parent route and would otherwise re-open
   * over the newly selected teammate.
   */
  public canDeactivate(): Observable<boolean> {
    return this.builderHost()?.canDeactivate() ?? of(true);
  }
}
